/**
 * Centralized Color Tokens
 *
 * Single source of truth for all dark/light mode colors across the website.
 * To change any color in the app, update ONLY this file.
 *
 * Usage in components:
 *   import { getTokens } from '../config/colorTokens'
 *   const { colorMode } = useColorMode()
 *   const t = getTokens(colorMode)
 *   // then use t.bg, t.border, t.textPrimary, etc.
 */

export const accentPalettes = [
	{
		id: 'blue',
		name: 'Blue',
		accent: '#2563EB',
		accentMid: '#0EA5E9',
		accentEnd: '#4F46E5',
		accentGlow: 'rgba(37, 99, 235, 0.42)',
		accentSoft: 'rgba(37, 99, 235, 0.1)',
		accentWash: 'rgba(14, 165, 233, 0.18)'
	},
	{
		id: 'emerald',
		name: 'Emerald',
		accent: '#10B981',
		accentMid: '#14B8A6',
		accentEnd: '#22C55E',
		accentGlow: 'rgba(16, 185, 129, 0.5)',
		accentSoft: 'rgba(16, 185, 129, 0.12)',
		accentWash: 'rgba(16, 185, 129, 0.2)'
	},
	{
		id: 'violet',
		name: 'Violet',
		accent: '#8B5CF6',
		accentMid: '#A855F7',
		accentEnd: '#EC4899',
		accentGlow: 'rgba(139, 92, 246, 0.5)',
		accentSoft: 'rgba(139, 92, 246, 0.12)',
		accentWash: 'rgba(139, 92, 246, 0.2)'
	},
	{
		id: 'rose',
		name: 'Rose',
		accent: '#F43F5E',
		accentMid: '#EC4899',
		accentEnd: '#F97316',
		accentGlow: 'rgba(244, 63, 94, 0.5)',
		accentSoft: 'rgba(244, 63, 94, 0.12)',
		accentWash: 'rgba(244, 63, 94, 0.2)'
	},
	{
		id: 'amber',
		name: 'Amber',
		accent: '#F59E0B',
		accentMid: '#F97316',
		accentEnd: '#EAB308',
		accentGlow: 'rgba(245, 158, 11, 0.5)',
		accentSoft: 'rgba(245, 158, 11, 0.12)',
		accentWash: 'rgba(245, 158, 11, 0.2)'
	},
	{
		id: 'cyan',
		name: 'Cyan',
		accent: '#06B6D4',
		accentMid: '#0EA5E9',
		accentEnd: '#10B981',
		accentGlow: 'rgba(6, 182, 212, 0.5)',
		accentSoft: 'rgba(6, 182, 212, 0.12)',
		accentWash: 'rgba(6, 182, 212, 0.2)'
	}
]

export const defaultAccentId = accentPalettes[0].id

export const getAccentPalette = (accentId = defaultAccentId) => {
	return accentPalettes.find((palette) => palette.id === accentId) || accentPalettes[0]
}

const colorTokens = {
	// ── Backgrounds ──────────────────────────────────────────
	// Deep midnight base with a subtle violet undertone for a rich, aesthetic feel
	bg: { dark: '#05050A', light: '#FCFCFE' },
	surface: { dark: '#101018', light: '#FFFFFF' },
	surfaceAlpha: { dark: 'rgba(16, 16, 24, 0.4)', light: 'rgba(255, 255, 255, 0.6)' },

	// ── Borders ──────────────────────────────────────────────
	// Soft, tinted borders instead of harsh grays
	border: { dark: '#20202E', light: '#ECECF2' },
	borderSubtle: { dark: 'whiteAlpha.100', light: 'blackAlpha.50' },
	borderCard: { dark: '#1E1E2A', light: '#ECECF2' },

	// ── Text ─────────────────────────────────────────────────
	// Soft ghost-white for readability and elegance
	textPrimary: { dark: '#F0F0F6', light: '#0F0F14' },
	textSecondary: { dark: '#9D9DBA', light: '#5A5A6E' },
	textMuted: { dark: '#727288', light: '#828292' },
	textSubtle: { dark: 'whiteAlpha.500', light: 'blackAlpha.500' },
	textFaint: { dark: 'whiteAlpha.200', light: 'blackAlpha.200' },

	// ── Interactive / Hover ──────────────────────────────────
	hoverBg: { dark: '#1C1C2A', light: '#F5F5FA' },
	hoverBgSubtle: { dark: 'whiteAlpha.100', light: 'blackAlpha.50' },
	iconDefault: { dark: '#C6C6D8', light: '#4A4A5A' },
	divider: { dark: 'whiteAlpha.100', light: 'blackAlpha.100' },
	groupLabel: { dark: 'whiteAlpha.400', light: 'blackAlpha.400' },

	// ── Navbar / Glass ───────────────────────────────────────
	navBg: { dark: 'rgba(7, 7, 12, 0.62)', light: 'rgba(255, 255, 255, 0.68)' },
	navShadow: {
		dark: '0 18px 48px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.04)',
		light: '0 18px 48px rgba(40,40,60,0.08), inset 0 1px 0 rgba(255,255,255,0.7)'
	},

	// ── Modals / Menus ───────────────────────────────────────
	modalBg: { dark: '#05050A', light: '#FFFFFF' },
	menuBg: { dark: 'rgba(8, 8, 12, 0.92)', light: 'rgba(255, 255, 255, 0.92)' },
	menuShadow: {
		dark: '0 20px 60px rgba(0, 0, 0, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
		light: '0 20px 60px rgba(30, 30, 45, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.85)'
	},

	// ── Tooltip ──────────────────────────────────────────────
	tooltipBg: { dark: '#20202E', light: '#0F0F14' },
	tooltipColor: { dark: '#F0F0F6', light: '#FCFCFE' },

	// ── Scrollbar ────────────────────────────────────────────
	scrollThumb: { dark: '#20202E', light: '#D4D4E0' },

	// ── Particles ────────────────────────────────────────────
	particle: { dark: '#38384A', light: '#D4D4E0' },

	// ── Accent (user-selectable) ─────────────────────────────
	accent: 'var(--site-accent, #2563EB)',
	accentMid: 'var(--site-accent-mid, #0EA5E9)',
	accentEnd: 'var(--site-accent-end, #4F46E5)',
	accentGlow: 'var(--site-accent-glow, rgba(37, 99, 235, 0.42))',
	accentSoft: 'var(--site-accent-soft, rgba(37, 99, 235, 0.1))',
	accentWash: 'var(--site-accent-wash, rgba(14, 165, 233, 0.18))',
	accentGradient: 'linear(to-r, var(--site-accent, #2563EB), var(--site-accent-mid, #0EA5E9), var(--site-accent-end, #4F46E5))'
}

/**
 * Resolve all tokens for the current color mode.
 * Returns a flat object where each key is resolved to its dark or light value.
 *
 * @param {"dark" | "light"} colorMode
 * @returns {Record<string, string>}
 */
export const getTokens = (colorMode) => {
	const resolved = {}
	for (const [key, value] of Object.entries(colorTokens)) {
		if (typeof value === 'object' && value !== null && 'dark' in value) {
			resolved[key] = colorMode === 'dark' ? value.dark : value.light
		} else {
			resolved[key] = value
		}
	}
	return resolved
}

export default colorTokens
